/**
 * The engagement plan card (agent step 3): each SOW item, the Clarid capability it maps to with its
 * trust status, and what Clarid will do (before approval) or did (after). The engagement lead approves
 * the plan; the approval and the exact plan are recorded before anything runs.
 */
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  PlayIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { agentApi, type EngagementCardState, type EngagementItemState } from '../../lib/agent';
import { shell, primaryBtn, secondaryBtn, useRefresh } from './cardKit';
import DeliverableSignoff from './DeliverableSignoff';

const TRUST: Record<EngagementItemState['trust'], { label: string; cls: string }> = {
  validated: { label: 'Validated', cls: 'bg-emerald-50 text-emerald-700' },
  beta: { label: 'Beta', cls: 'bg-amber-50 text-amber-700' },
  advisor: { label: 'Advisor', cls: 'bg-gray-100 text-gray-600' },
  not_available: { label: 'Not available', cls: 'bg-gray-100 text-gray-500' },
  record: { label: 'Bank record', cls: 'bg-sky-50 text-sky-700' },
  bank: { label: 'Bank supplies', cls: 'bg-gray-100 text-gray-600' },
};

const PLAN_STATE: Record<EngagementItemState['state'], { label: string; cls: string }> = {
  READY: { label: 'Clarid runs it', cls: 'text-ocean-700' },
  NEEDS_DATA: { label: 'Needs data first', cls: 'text-amber-700' },
  IN_CLARID: { label: 'Done in Clarid', cls: 'text-gray-700' },
  ADVISOR: { label: 'Advisor', cls: 'text-gray-500' },
  BANK_SUPPLIES: { label: 'Bank supplies', cls: 'text-gray-600' },
  NOT_AVAILABLE: { label: 'Not available', cls: 'text-gray-500' },
};

const PLAN_STATUS: Record<EngagementCardState['status'], { label: string; cls: string }> = {
  PROPOSED: { label: 'Awaiting approval', cls: 'bg-amber-50 text-amber-800' },
  RUNNING: { label: 'Running', cls: 'bg-ocean-50 text-ocean-800' },
  COMPLETE: { label: 'Complete', cls: 'bg-emerald-50 text-emerald-800' },
  REJECTED: { label: 'Not approved', cls: 'bg-gray-100 text-gray-600' },
};

const SUMMARY: [EngagementItemState['state'], string][] = [
  ['READY', 'Clarid runs'],
  ['NEEDS_DATA', 'need data first'],
  ['IN_CLARID', 'done in Clarid'],
  ['ADVISOR', 'advisor'],
  ['BANK_SUPPLIES', 'the bank supplies'],
  ['NOT_AVAILABLE', 'not available'],
];

export default function EngagementCard({ teamId, card, queryKey }: { teamId: string; card: EngagementCardState; queryKey: unknown[] }) {
  const refresh = useRefresh(queryKey);
  // Errors are shown by the API layer; refresh in case the plan moved on.
  const approve = useMutation({ mutationFn: () => agentApi.approveEngagement(teamId, card.id), onSuccess: refresh, onError: refresh });
  const reject = useMutation({ mutationFn: () => agentApi.rejectEngagement(teamId, card.id), onSuccess: refresh });
  const busy = approve.isPending || reject.isPending;
  const count = (st: EngagementItemState['state']) => card.items.filter((i) => i.state === st).length;
  const summary = SUMMARY.filter(([st]) => count(st) > 0).map(([st, label]) => `${count(st)} ${label}`).join(' · ');
  const status = PLAN_STATUS[card.status];
  const isLetter = card.engagementKind === 'REQUEST_LETTER';
  const isPlaybook = card.engagementKind === 'PLAYBOOK';
  // The plan's fair lending items share one stored report; it is signed off here.
  const deliverableIds = [...new Set(card.items.map((i) => i.result?.deliverableId).filter((id): id is string => !!id))];

  return (
    <div className={clsx(shell, 'border-gray-200 px-0 py-0')}>
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3">
        <ClipboardDocumentCheckIcon className="h-5 w-5 text-ocean-600" />
        <p className="font-medium text-navy-900">{card.title}</p>
        {isPlaybook && <span className="rounded-full bg-ocean-50 px-2 py-0.5 text-xs text-ocean-800">Playbook</span>}
        {isLetter && (
          <span className="rounded-full bg-navy-900/5 px-2 py-0.5 text-xs text-navy-900">
            Exam request letter{card.regulator ? ` · ${card.regulator}` : ''}{card.dueDate ? ` · due ${card.dueDate}` : ''}
          </span>
        )}
        {card.sowFileName && (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
            <PaperClipIcon className="h-3 w-3" /> {card.sowFileName}
          </span>
        )}
        <span className={clsx('ml-auto rounded-full px-2 py-0.5 text-xs font-medium', status.cls)}>{status.label}</span>
      </div>
      <p className="px-4 pt-2 text-xs text-gray-500">
        {card.items.length} {isLetter ? 'request' : 'SOW'} items: {summary}
      </p>

      <div className="overflow-x-auto px-4 py-2">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="w-6 py-1.5 pr-2 font-medium">#</th>
              <th className="py-1.5 pr-3 font-medium">{isLetter ? 'Request item' : 'SOW item'}</th>
              <th className="py-1.5 pr-3 font-medium">Clarid</th>
              <th className="py-1.5 font-medium">{card.status === 'PROPOSED' ? 'Plan' : 'Result'}</th>
            </tr>
          </thead>
          <tbody>
            {card.items.map((i) => (
              <tr key={i.id} className="border-t border-gray-100 align-top">
                <td className="py-2 pr-2 text-gray-400">{i.ordinal}</td>
                <td className="py-2 pr-3 text-gray-800">
                  <span className="line-clamp-3">{i.sowText}</span>
                </td>
                <td className="py-2 pr-3">
                  <span className="text-gray-700">{i.capabilityName}</span>{' '}
                  <span className={clsx('inline-block rounded px-1.5 py-0.5 text-[10px] font-medium', (TRUST[i.trust] ?? TRUST.not_available).cls)}>
                    {(TRUST[i.trust] ?? TRUST.not_available).label}
                  </span>
                </td>
                <td className="py-2">
                  <ItemOutcome item={i} planStatus={card.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {card.status === 'PROPOSED' && (
        <div className="border-t border-gray-100 px-4 py-3">
          <div className="flex gap-2">
            <button type="button" className={primaryBtn} disabled={busy || (card.engagementKind === 'SOW' && count('READY') === 0)} onClick={() => approve.mutate()}>
              <PlayIcon className="h-4 w-4" /> {approve.isPending ? 'Starting…' : isLetter ? 'Approve and prepare the response' : 'Approve plan and run'}
            </button>
            <button type="button" className={secondaryBtn} disabled={busy} onClick={() => reject.mutate()}>
              Not now
            </button>
          </div>
          <p className="mt-1.5 text-xs text-gray-400">
            {isLetter
              ? 'An Owner or Compliance Officer approves. Clarid then produces what it can and builds a response index of every request item. The approval and this exact plan are recorded first.'
              : 'The engagement lead (an Owner or Compliance Officer) approves. The approval and this exact plan are recorded before anything runs.'}
          </p>
        </div>
      )}
      {card.indexDeliverableId && (
        <div className="border-t border-gray-100 px-4 pb-3 pt-2">
          <p className="text-xs font-medium text-navy-900">
            {isLetter ? 'Response index: every request item' : 'Index: every item'}, the file provided (with its fingerprint) or who supplies it
          </p>
          <DeliverableSignoff teamId={teamId} deliverableId={card.indexDeliverableId} />
        </div>
      )}
      {deliverableIds.length > 0 && (
        <div className="border-t border-gray-100 px-4 pb-3 pt-1">
          {deliverableIds.map((id) => <DeliverableSignoff key={id} teamId={teamId} deliverableId={id} />)}
        </div>
      )}
      {card.analysisId && card.status !== 'PROPOSED' && card.status !== 'REJECTED' && (
        <div className="border-t border-gray-100 px-4 py-2 text-xs">
          <Link to={`/fair-lending/fld/${card.analysisId}`} className="font-medium text-ocean-700 hover:text-ocean-800">
            View the fair lending results
          </Link>
        </div>
      )}
    </div>
  );
}

function ItemOutcome({ item, planStatus }: { item: EngagementItemState; planStatus: EngagementCardState['status'] }) {
  const plan = PLAN_STATE[item.state];
  if (planStatus === 'PROPOSED' || planStatus === 'REJECTED' || item.status === 'SKIPPED' || item.status === 'PENDING') {
    return (
      <span>
        <span className={clsx('font-medium', plan.cls)}>{plan.label}.</span> <span className="text-gray-500">{item.detail}</span>
      </span>
    );
  }
  if (item.status === 'DELIVERING') {
    return (
      <span className="inline-flex items-center gap-1 text-gray-600">
        <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-ocean-600" /> Preparing the report
      </span>
    );
  }
  if (item.status === 'RUNNING') {
    return (
      <span className="inline-flex items-center gap-1 text-gray-600">
        <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-ocean-600" /> Running
      </span>
    );
  }
  if (item.status === 'FAILED') {
    return (
      <span className="inline-flex items-start gap-1 text-red-700">
        <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" /> {item.error ?? 'It did not finish.'}
      </span>
    );
  }
  const path = item.result?.downloadPath;
  if (path) {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 text-left font-medium text-ocean-700 hover:text-ocean-800"
        onClick={() => agentApi.download(path, 'Fair_Lending_FLD.docx').catch(() => undefined)}
      >
        <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0" /> FLD report with the evidence appendix
      </button>
    );
  }
  if (item.result?.link) {
    return (
      <Link to={item.result.link} className="inline-flex items-center gap-1 font-medium text-ocean-700 hover:text-ocean-800">
        <CheckCircleIcon className="h-3.5 w-3.5" /> Done: open it
      </Link>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-emerald-700">
      <CheckCircleIcon className="h-3.5 w-3.5" /> Done
    </span>
  );
}
